import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const titleCase = (value) =>
  String(value || '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const money = (value, currency = 'GBP') =>
  value == null
    ? '—'
    : new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency,
      }).format(Number(value));

const Price = ({ amount, currency, included = false }) => (
  <span className={included ? 'font-medium text-success' : 'text-foreground'}>
    {included ? 'Included' : money(amount, currency)}
  </span>
);

const ConsultantPricingSchedule = ({ schedule, className = '' }) => {
  if (!schedule) return null;
  const levels = schedule.levels || [];

  return (
    <Card className={`bg-card ${className}`}>
      <CardHeader>
        <CardTitle className='text-base'>Consultant Pricing Schedule · version {schedule.version}</CardTitle>
        <p className='text-xs text-muted-foreground'>
          Effective {schedule.effectiveFrom} · {schedule.currency} · accepted content hash {schedule.contentHash}
        </p>
      </CardHeader>
      <CardContent className='space-y-5'>
        <p className='text-sm text-foreground'>
          This complete schedule is incorporated into the Consultant Master Agreement. Each client is later allocated
          one Plan, one FTE band and any Add-ons from these accepted prices.
        </p>

        <div className='overflow-x-auto rounded-md border border-border'>
          <Table>
            <TableHeader>
              <TableRow className='border-border'>
                <TableHead>Plan</TableHead>
                <TableHead>FTE</TableHead>
                <TableHead className='text-right'>Standard</TableHead>
                {levels.map((level) => (
                  <TableHead key={level.id} className='text-right'>
                    {titleCase(level.id)} ({level.discountPercent}% off)
                  </TableHead>
                ))}
                <TableHead className='text-right'>Points</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(schedule.plans || []).flatMap((plan) =>
                (schedule.fteBands || []).map((band) => {
                  const cell = schedule.matrix?.[plan]?.[band];
                  return (
                    <TableRow key={`${plan}-${band}`} className='border-border'>
                      <TableCell className='font-medium'>{titleCase(plan)}</TableCell>
                      <TableCell>{band}</TableCell>
                      <TableCell className='text-right'>{money(cell?.standard, schedule.currency)}</TableCell>
                      {levels.map((level) => (
                        <TableCell key={level.id} className='text-right'>
                          <Price amount={cell?.levels?.[level.id]} currency={schedule.currency} />
                        </TableCell>
                      ))}
                      <TableCell className='text-right'>{cell?.points}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        <div>
          <h3 className='mb-2 text-sm font-semibold text-foreground'>Add-ons</h3>
          <div className='overflow-x-auto rounded-md border border-border'>
            <Table>
              <TableHeader>
                <TableRow className='border-border'>
                  <TableHead>Add-on</TableHead>
                  <TableHead className='text-right'>Standard</TableHead>
                  {levels.map((level) => (
                    <TableHead key={level.id} className='text-right'>{titleCase(level.id)}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(schedule.addOns || []).map((addOn) => {
                  const annual = addOn.standardAnnualPrice != null;
                  return (
                    <TableRow key={addOn.id} className='border-border'>
                      <TableCell>
                        <p className='font-medium'>{addOn.name}</p>
                        {addOn.description && <p className='text-xs text-muted-foreground'>{addOn.description}</p>}
                        {!!addOn.includedPlanIds?.length && (
                          <p className='text-xs text-success'>
                            Included with {addOn.includedPlanIds.map(titleCase).join(' and ')}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className='text-right'>
                        {money(annual ? addOn.standardAnnualPrice : addOn.standardMonthlyPrice, schedule.currency)}
                        <span className='text-xs text-muted-foreground'> / {annual ? 'year' : 'month'}</span>
                      </TableCell>
                      {levels.map((level) => {
                        const price = addOn.levelPrices?.[level.id] || {};
                        return (
                          <TableCell key={level.id} className='text-right'>
                            <Price
                              amount={annual ? price.annual : price.monthly}
                              currency={schedule.currency}
                              included={price.included}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className='rounded-md bg-muted p-4 text-sm text-foreground'>
          <p>Growth: 20 clients and 50 points. Scale: 50 clients and 140 points.</p>
          <p>Assessed monthly, with three-assessment downgrade protection.</p>
          <p>Growth and Scale include Premium Factors. A 36-month client term receives another 5% off.</p>
          <p>{schedule.programme?.masterNoticeDays} days’ notice; monthly charges with annual minimum true-up.</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ConsultantPricingSchedule;
